import * as React from 'react';
import { Box, Button, Grid, Stack, ThemeProvider, Typography } from '@mui/material';
import { generateTheme } from '../themes';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from "gsap";

const CustomerLogos = ({ data }) => {
    const {
        eyebrow,
        title,
        ctaText,
        ctaLink,
        colorTheme = 'white',
        logos = []
    } = data;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;

        const links = gsap.utils.toArray(".customerLogo");

        links.forEach((a, index) => {
            gsap.fromTo(
                a,
                { opacity: 0, y: 10 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.4,
                    delay: 0.05 * index,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: a,
                        start: 'top 92%',
                        markers: false,
                    },
                }
            );
        });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    background: (theme) => theme.palette.background.default,
                    px: { xs: 2, md: 12 },
                    py: { xs: 4, md: 6 }
                }}
            >
                <Stack
                    direction="column"
                    sx={{
                        width: "100%",
                        textAlign: "center"
                    }}
                >
                    {eyebrow && (
                        <Typography
                            variant="overline"
                            component="span"
                            sx={{
                                color: 'text.secondary',
                                letterSpacing: '0.15em',
                                fontSize: '0.8rem',
                                fontWeight: 400,
                                opacity: 0.6,
                                mb: 1
                            }}
                            dangerouslySetInnerHTML={{ __html: eyebrow }}
                        />
                    )}

                    {title && (
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                color: 'text.primary',
                                fontFamily: 'Kanit, sans-serif',
                                fontWeight: 800,
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: '0.01em',
                                width: '100%',
                                fontSize: { xs: '2rem', md: '3rem', lg: '3.5rem' },
                                lineHeight: 1.1,
                                mb: { xs: 4, md: 6 }
                            }}
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                    )}

                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        rowSpacing={{ xs: 4, md: 5 }}
                        sx={{
                            width: "100%",
                            textAlign: "center",
                            mt: 2
                        }}
                    >
                        {logos.map((logo, index) => (
                            <Grid
                                className="customerLogo"
                                item
                                xs={6}
                                sm={3}
                                md={3}
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: { xs: 1.5, md: 3 }
                                }}
                                onClick={() => {
                                    if (logo.url) window.open(logo.url, '_blank');
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: { xs: 120, md: 140 },
                                        height: { xs: 44, md: 52 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={logo.imageUrl}
                                        alt={logo.imageAlt || 'Customer logo'}
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            width: "auto",
                                            height: "auto",
                                            objectFit: "contain",
                                            cursor: logo.url ? "pointer" : "default",
                                            filter: "grayscale(1)",
                                            opacity: 0.7,
                                            transition: "filter 0.3s ease, opacity 0.3s ease",
                                            "&:hover": {
                                                filter: "grayscale(0)",
                                                opacity: 1
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {ctaText && ctaLink && (
                        <Box sx={{ mt: { xs: 4, md: 5 }, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                href={ctaLink}
                                variant="outlined"
                                sx={{
                                    color: 'primary.main',
                                    borderColor: 'primary.main',
                                    borderWidth: 1.5,
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    px: 3,
                                    py: 1,
                                    '&:hover': {
                                        borderWidth: 1.5,
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(92, 51, 215, 0.08)'
                                    }
                                }}
                            >
                                <span dangerouslySetInnerHTML={{ __html: ctaText }} />
                                <span aria-hidden="true" style={{ marginLeft: '0.35em' }}>→</span>
                            </Button>
                        </Box>
                    )}
                </Stack>
            </Box>
        </ThemeProvider>
    );
};

export default CustomerLogos;
